import React from 'react';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

const KPIBox = ({ 
  title, 
  value, 
  delta, 
  percentage, 
  type = 'units', // 'units', 'currency'
  icon: Icon, // New prop for MUI icon component
  color = 'primary', // New prop: 'primary', 'warning', 'error', 'success', 'info'
  sx = {},
  showComparisonHint = true
}) => {
  const { t, i18n } = useTranslation();
  const jsLocale = { es: 'es-ES', en: 'en-US', it: 'it-IT' }[i18n.language] || 'es-ES';
  const comparisonHint = showComparisonHint ? t('weekToDateComparisonHint') : null;
  const hasDelta = delta !== null && delta !== undefined && delta !== 0;
  
  // Color schemes for different metric types
  const colorSchemes = {
    error: {
      background: '#fbfbfa',
      iconBg: 'transparent',
      iconColor: '#dc2626',
      border: '#f0efee'
    },
    warning: {
      background: '#fbfbfa',
      iconBg: 'transparent',
      iconColor: '#f59e0b',
      border: '#f0efee'
    },
    info: {
      background: '#fbfbfa',
      iconBg: 'transparent',
      iconColor: '#f59e0b', // Orange for under review
      border: '#f0efee'
    },
    success: {
      background: '#fbfbfa',
      iconBg: 'transparent',
      iconColor: '#16a34a',
      border: '#f0efee'
    },
    primary: {
      background: '#fbfbfa',
      iconBg: 'transparent',
      iconColor: '#6b7280', // Neutral grey for total alerts
      border: '#f0efee'
    }
  };
  
  const scheme = colorSchemes[color] || colorSchemes.primary;
  
  // Format the main value based on type
  const formatValue = (val) => {
    if (val === null || val === undefined || val === 0) return '-';

    if (type === 'currency') {
      const absVal = Math.abs(val);
      const sign = val < 0 ? '-' : '';
      // Use 2 decimal places for small values so €0.45 doesn't round to €0
      const formatted = absVal < 10
        ? absVal.toFixed(2)
        : Math.round(absVal).toLocaleString(jsLocale);
      return `${sign}€${formatted}`;
    }

    // Round to nearest whole number for units
    return Math.round(Math.abs(val)).toLocaleString();
  };
  
  // Format delta value
  const formatDelta = (val) => {
    if (val === null || val === undefined) return null;

    if (type === 'currency') {
      const roundedVal = Math.round(val);
      if (roundedVal >= 0) {
        return `+€${roundedVal.toLocaleString(jsLocale)}`;
      } else {
        return `-€${Math.abs(roundedVal).toLocaleString(jsLocale)}`;
      }
    }

    const prefix = val >= 0 ? '+' : '';
    return `${prefix}${Math.round(val).toLocaleString()}`;
  };

  // Format percentage
  const formatPercentage = (val) => {
    if (val === null || val === undefined) return null;

    const prefix = val >= 0 ? '+' : '';
    return `${prefix}${Math.round(val)}%`;
  };
  
  const isPositive = delta >= 0;
  const deltaColor = isPositive ? '#448361' : '#d9534f';
  
  return (
    <Box sx={{
      flex: 1,
      minWidth: { xs: '100%', sm: 160 },
      backgroundColor: scheme.background,
      border: `1px solid ${scheme.border}`,
      borderRadius: '12px',
      p: { xs: 2, sm: 2.5 },
      transition: 'all 0.2s ease',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
      },
      ...sx
    }}>
      {/* Header with title and icon inline */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <Typography sx={{ 
          fontSize: '13px',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
          fontWeight: 500,
          color: '#64748b',
          letterSpacing: '-0.01em'
        }}>
          {title}
        </Typography>
        
        {Icon && (
          <Icon sx={{ 
            fontSize: 16, 
            color: scheme.iconColor 
          }} />
        )}
      </Box>
      
      {/* Main Value */}
      <Typography sx={{ 
        fontSize: '28px',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
        fontWeight: 600,
        color: '#1e293b',
        letterSpacing: '-0.02em',
        lineHeight: 1.2,
        mb: 1
      }}>
        {formatValue(value)}
      </Typography>
      
      {/* Delta Row */}
      {hasDelta && (
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
          <Typography sx={{ 
            fontSize: '13px',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
            fontWeight: 400,
            color: '#64748b',
            letterSpacing: '-0.01em'
          }}>
            {t('incremental')}:
          </Typography>
          <Typography sx={{ 
            fontSize: '14px',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
            fontWeight: 500,
            color: deltaColor,
            letterSpacing: '-0.01em'
          }}>
            {formatDelta(delta)}
          </Typography>
          {percentage !== null && percentage !== undefined && (
            <Typography sx={{ 
              fontSize: '13px',
              fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
              fontWeight: 500,
              color: deltaColor,
              letterSpacing: '-0.01em'
            }}>
              ({formatPercentage(percentage)})
            </Typography>
          )}
        </Box>
      )}

      {hasDelta && showComparisonHint && (
        <Typography sx={{ 
          fontSize: '11px',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
          fontWeight: 400,
          color: '#94a3b8',
          letterSpacing: '-0.01em',
          mt: 0.75
        }}>
          {comparisonHint}
        </Typography>
      )}
    </Box>
  );
};

export default KPIBox;
